/**
 * Maps a FIELD_OPTIONS key to its finite/enum value set, for fields the constant editor
 * dialog can show as a searchable dropdown instead of free text (CU-86can7jwy: "Fix XLS
 * import: required column mapping dropdown for enum fields").
 *
 * Deliberately reuses value sets already exported from @ooh/contracts (the same source the
 * create/update media-unit forms validate against) instead of inventing new domain
 * metadata — `type`, `material`, and `lighting` are the only FIELD_OPTIONS keys with an
 * existing STATIC closed value set. Other tak/nie-style boolean fields (mural_3d,
 * mural_led_tape, mural_neon_led, mural_fluo_uv) have no such exported constant yet, so they
 * intentionally fall back to free text here rather than a hardcoded guess.
 *
 * `format` is a fourth enum-backed field but its value set is a per-tenant dictionary (GET
 * /media-formats), not a static export — see `isQueryBackedEnumFieldKey` below and
 * `hooks/use-format-enum-options.ts` for how its options are resolved (fix: media-import
 * wizard "Format" constant editor rendered free text instead of an enum-aware dropdown,
 * unlike type/material/lighting).
 */
import { MediaTypeEnum, MATERIAL_VALUES, LIGHTING_VALUES } from '@ooh/contracts';

const ENUM_VALUES_BY_FIELD_KEY: Record<string, readonly string[]> = {
  type: MediaTypeEnum.options,
  material: MATERIAL_VALUES,
  lighting: LIGHTING_VALUES,
};

/** FIELD_OPTIONS keys whose closed value set must be fetched (not statically known). */
const QUERY_BACKED_ENUM_FIELD_KEYS = new Set<string>(['format']);

/** The closed value set for `fieldKey`, or undefined when the field is open-ended (free text)
 * or query-backed (see `isQueryBackedEnumFieldKey`). */
export function getEnumValuesForFieldKey(fieldKey: string): readonly string[] | undefined {
  return ENUM_VALUES_BY_FIELD_KEY[fieldKey];
}

/** True when `fieldKey`'s closed value set must be fetched from the API (currently only
 * 'format', via GET /media-formats) instead of read synchronously from this module. */
export function isQueryBackedEnumFieldKey(fieldKey: string): boolean {
  return QUERY_BACKED_ENUM_FIELD_KEYS.has(fieldKey);
}
